
import { defineComponent } from 'vue'

import type { ApolloQueryResult } from 'apollo-client'
import type { FetchResult } from 'apollo-link'

import DetachSavedCardMutation from '~/apollo/mutations/owner/detachSavedCard.gql'
import SetDefaultCardMutation from '~/apollo/mutations/owner/setDefaultCard.gql'
import UpdateAutoTopupMutation from '~/apollo/mutations/owner/updateAutoTopup.gql'
import UnifiedBillingQuery from '~/apollo/queries/owner/unifiedBilling.gql'
import UnifiedBillingCardsQuery from '~/apollo/queries/owner/unifiedBillingCards.gql'
import UnifiedBillingCreditsQuery from '~/apollo/queries/owner/unifiedBillingCredits.gql'
import {
  BillingInterval,
  InvoiceStatus,
  UnifiedProductPlan,
  type DetachSavedCardMutation as DetachSavedCardMutationData,
  type SetDefaultCardMutation as SetDefaultCardMutationData,
  type UnifiedBillingQuery as UnifiedBillingQueryData,
  type UpdateAutoTopupMutation as UpdateAutoTopupMutationData
} from '~/types/types'
import { centsToDollars } from '~/utils/billing'
import { formatDate } from '~/utils/date'

import OwnerDetailMixin from '~/mixins/ownerDetailMixin'

import type { V2PlanConfig } from './v2PlanConfig'
import { ENTERPRISE_PLAN, OPEN_SOURCE_PLAN, TEAM_PLAN } from './v2PlanConfig'

type UnifiedSubscription = NonNullable<
  NonNullable<UnifiedBillingQueryData['owner']>['productSubscription']
>
type UnifiedProductData = Extract<UnifiedSubscription['product'], { __typename?: 'UnifiedProduct' }>

export default defineComponent({
  name: 'BillingV2Page',
  mixins: [OwnerDetailMixin],
  data() {
    return {
      loading: true,
      cardsLoading: false,
      subscription: null as UnifiedSubscription | null,
      showAddCardModal: false,
      showAddCreditModal: false,
      cardToDelete: null as string | null,
      deletingCard: false
    }
  },
  async fetch() {
    const { owner, provider } = this.$route.params
    try {
      const refetch = true
      const response: ApolloQueryResult<UnifiedBillingQueryData> = await this.$fetchGraphqlData(
        UnifiedBillingQuery,
        {
          login: owner,
          provider: this.$providerMetaMap[provider].value
        },
        refetch
      )
      this.subscription = response.data?.owner?.productSubscription ?? null
    } catch (error) {
      this.$logErrorAndToast(
        error as Error,
        'We encountered an error while fetching your billing details. Please try again later.'
      )
    } finally {
      this.loading = false
    }
  },
  computed: {
    ownerId(): string {
      return (this as unknown as { owner: { id: string } }).owner?.id ?? ''
    },
    product(): UnifiedProductData | null {
      const p = this.subscription?.product
      if (p?.__typename === 'UnifiedProduct') return p
      return null
    },
    billingDetails() {
      return this.subscription?.billingDetails ?? null
    },
    planType(): 'enterprise' | 'team' | 'free' {
      if (!this.product) return 'free'
      if (this.product.plan === UnifiedProductPlan.PlanEnterprise) return 'enterprise'
      if (
        this.product.plan === UnifiedProductPlan.PlanTeam ||
        this.product.plan === UnifiedProductPlan.PlanTrial
      ) {
        return 'team'
      }
      return 'free'
    },
    isEnterprise(): boolean {
      return this.planType === 'enterprise'
    },
    isActivePlan(): boolean {
      return this.planType === 'team'
    },
    isTrial(): boolean {
      return this.product?.plan === UnifiedProductPlan.PlanTrial
    },
    formattedBillingInterval(): string {
      if (!this.billingDetails?.billingInterval) return ''
      return this.billingDetails.billingInterval === BillingInterval.Monthly ? 'Monthly' : 'Yearly'
    },
    formattedNextRenewal(): string {
      if (!this.billingDetails?.nextBillingCycle) return ''
      return formatDate(this.billingDetails.nextBillingCycle)
    },
    enterpriseActivePlan() {
      return {
        name: 'Enterprise plan',
        price: 'Custom pricing',
        contributors: this.product?.seatQuantity ?? 0,
        ossTargets: this.product?.scaTargetQuantity ?? 0,
        aiCreditBalance: centsToDollars(this.billingDetails?.usageCredits ?? 0),
        nextRenewal: this.formattedNextRenewal,
        billingInterval: this.formattedBillingInterval || 'Yearly'
      }
    },
    activePlan() {
      return {
        name: 'Team plan',
        price: TEAM_PLAN.price ?? '',
        contributors: this.product?.seatQuantity ?? 0,
        ossTargets: this.product?.scaTargetQuantity ?? 0,
        aiCreditBalance: centsToDollars(this.billingDetails?.usageCredits ?? 0),
        autoRechargeEnabled: this.product?.isAutoTopupEnabled ?? false,
        balanceThreshold: this.product?.autoTopupThreshold ?? 0,
        autoRechargeAmount: this.product?.autoTopupAmount ?? 0,
        maxRechargePerMonth: this.product?.autoTopupMax ?? 0,
        nextRenewal: this.formattedNextRenewal,
        billingInterval: this.isTrial ? '' : this.formattedBillingInterval || 'Monthly',
        promotionalCredits: centsToDollars(this.billingDetails?.promotionalCredits ?? 0),
        currentInvoiceAmount: centsToDollars(this.billingDetails?.upcomingBillingAmount ?? 0)
      }
    },
    paymentMethods(): Array<{
      id: string
      brand: string
      brandLabel: string
      lastFour: string
      expiryLabel: string
      expiryShort: string
      isDefault: boolean
    }> {
      const cards = this.billingDetails?.cards
      if (!cards?.length) return []
      return cards
        .filter((c): c is NonNullable<typeof c> => Boolean(c?.endingIn))
        .map((card) => {
          const brand = card.brand ?? 'card'
          return {
            id: card.id ?? '',
            brand: brand.toLowerCase(),
            brandLabel: brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase(),
            lastFour: card.endingIn!,
            expiryLabel:
              card.expMonth && card.expYear
                ? `Expires ${new Date(card.expYear, card.expMonth - 1).toLocaleString('en-US', {
                    month: 'long'
                  })} ${card.expYear}`
                : '',
            expiryShort:
              card.expMonth && card.expYear
                ? `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`
                : '',
            isDefault: card.isDefault ?? false
          }
        })
    },
    recentInvoices(): Array<{
      id: string
      date: string
      amount: number
      status: string
      url: string
    }> {
      const invoices = this.billingDetails?.invoices
      if (!invoices?.length) return []
      return invoices
        .filter((inv): inv is NonNullable<typeof inv> => Boolean(inv))
        .map((inv) => ({
          id: inv.invoiceId ?? '',
          date: inv.date ? formatDate(inv.date) : '',
          amount: centsToDollars(inv.amountCents ?? 0),
          status: this.formatInvoiceStatus(inv.status),
          url: inv.url ?? ''
        }))
    },
    currentPlan(): V2PlanConfig {
      return OPEN_SOURCE_PLAN
    },
    teamPlanConfig(): V2PlanConfig {
      return TEAM_PLAN
    },
    cardToDeleteDetails(): {
      brand: string
      brandLabel: string
      lastFour: string
      expiryLabel: string
    } | null {
      if (!this.cardToDelete) return null
      return this.paymentMethods.find((m) => m.id === this.cardToDelete) ?? null
    },
    enterprisePlanConfig(): V2PlanConfig {
      return ENTERPRISE_PLAN
    }
  },
  methods: {
    formatInvoiceStatus(status: InvoiceStatus | null | undefined): string {
      switch (status) {
        case InvoiceStatus.Paid:
          return 'Paid'
        case InvoiceStatus.Open:
          return 'Open'
        case InvoiceStatus.Draft:
          return 'Draft'
        case InvoiceStatus.Uncollectible:
          return 'Uncollectible'
        case InvoiceStatus.Void:
          return 'Void'
        default:
          return ''
      }
    },
    async refetchCards(): Promise<void> {
      this.cardsLoading = true
      try {
        const { owner, provider } = this.$route.params
        const response: ApolloQueryResult<UnifiedBillingQueryData> = await this.$fetchGraphqlData(
          UnifiedBillingCardsQuery,
          { login: owner, provider: this.$providerMetaMap[provider].value },
          true
        )
        const cards = response.data?.owner?.productSubscription?.billingDetails?.cards
        if (this.subscription?.billingDetails && cards) {
          this.subscription.billingDetails.cards = cards
        }
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'Failed to refresh payment methods. Please try again later.'
        )
      } finally {
        this.cardsLoading = false
      }
    },
    async refetchCredits(): Promise<void> {
      try {
        const { owner, provider } = this.$route.params
        const response: ApolloQueryResult<UnifiedBillingQueryData> = await this.$fetchGraphqlData(
          UnifiedBillingCreditsQuery,
          { login: owner, provider: this.$providerMetaMap[provider].value },
          true
        )
        const billingDetails = response.data?.owner?.productSubscription?.billingDetails
        if (this.subscription?.billingDetails && billingDetails) {
          this.subscription.billingDetails.usageCredits = billingDetails.usageCredits
          this.subscription.billingDetails.promotionalCredits = billingDetails.promotionalCredits
        }
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'Failed to refresh credit balance. Please try again later.'
        )
      }
    },
    navigateToCheckout(): void {
      const route = this.$generateRoute(['settings', 'billing', 'checkout'], false, true)
      this.$router.push(`${route}?plan=PLAN_TEAM&interval=monthly`)
    },
    async updateAutoTopup(settings: {
      isAutoTopupEnabled?: boolean
      autoTopupThreshold?: number
      autoTopupAmount?: number
      autoTopupMax?: number
    }) {
      try {
        ;(await this.$applyGraphqlMutation(UpdateAutoTopupMutation, {
          ownerId: this.owner?.id,
          ...settings
        })) as FetchResult<UpdateAutoTopupMutationData>
        await this.$fetch()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while updating auto top-up settings. Please try again later.'
        )
      }
    },
    async detachSavedCard() {
      if (!this.cardToDelete) return
      this.deletingCard = true
      try {
        ;(await this.$applyGraphqlMutation(DetachSavedCardMutation, {
          ownerId: this.owner?.id,
          paymentMethodId: this.cardToDelete
        })) as FetchResult<DetachSavedCardMutationData>
        this.cardToDelete = null
        this.$toast.success('Card removed successfully.')
        await this.refetchCards()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while removing the card. Please try again later.'
        )
      } finally {
        this.deletingCard = false
      }
    },
    async onCardAdded() {
      this.showAddCardModal = false
      this.$toast.success('Card added successfully.')
      const hadNoCards = this.paymentMethods.length === 0
      await this.refetchCards()
      if (hadNoCards && this.paymentMethods.length === 1) {
        await this.setDefaultCard(this.paymentMethods[0].id)
      }
    },
    async onCreditAdded() {
      this.showAddCreditModal = false
      this.$toast.success('Credit added successfully.')
      await this.refetchCredits()
    },
    async setDefaultCard(paymentMethodId: string) {
      try {
        ;(await this.$applyGraphqlMutation(SetDefaultCardMutation, {
          ownerId: this.owner?.id,
          paymentMethodId
        })) as FetchResult<SetDefaultCardMutationData>
        this.$toast.success('Default card updated.')
        await this.refetchCards()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while updating the default card. Please try again later.'
        )
      }
    }
  }
})
